import {Component} from 'preact';
import cx from 'classnames';
import Icon from '@components/common/Icon';
import {Cart} from '@modules/cart/Cart';
import QtyToggle from '@components/common/QtyToggle';
import CartService from '@services/CartService';
import debounce from 'lodash.debounce';
import {calculateMaxInputQuantityForProduct} from '@modules/products/util';
import {withItemListAttribution} from '@modules/cart/cartAttribution';
import appLogger from '@core/LoggingProvider';

class AddCartButton extends Component {

    constructor ( props ) {
        super( props );

        this._flushQuantityUpdate = this._flushQuantityUpdate.bind( this );
        this.onUpdateItem = debounce( this._flushQuantityUpdate, 1000 );
        this.onQuantityChange = this.onQuantityChange.bind( this );
        this.onAddItem = this.onAddItem.bind( this );
        this.onCartChange = this.onCartChange.bind( this );
        this._isMounted = false;
        this._pendingQuantity = null;
        this._queuedQuantity = null;
        this._quantityUpdatePromise = null;

        this.state = {
            processingAdd: false
        };
    }

    async componentDidMount () {
        this._isMounted = true;
        Cart.bind( 'cartChange', this.onCartChange );
        this.setMaxQuantity( {
            product: this.props.product
        } );
    }

    async componentWillReceiveProps ( nextProps, nextContext ) {
        this.setMaxQuantity( {product: nextProps.product} );
    }

    async onCartChange ( e, cart ) {
        if ( cart.product.sku === this.props.product.sku ) {
            const cartQuantity = parseInt( cart.quantity, 10 );

            if ( this._pendingQuantity !== null ) {
                if ( cartQuantity !== this._pendingQuantity ) {
                    return;
                }

                this._pendingQuantity = null;
            }

            this.setMaxQuantity( {
                product: this.props.product,
                inCartQuantity: cartQuantity
            } );
        }
    }

    componentWillUnmount () {
        this._isMounted = false;
        this.onUpdateItem.cancel();
        this._pendingQuantity = null;
        this._queuedQuantity = null;
        Cart.unbind( 'cartChange', this.onCartChange );
    }

    async setMaxQuantity ( {product, inCartQuantity = null} ) {
        const cartState = await calculateMaxInputQuantityForProduct( {product, inCartQuantity} );
        const hasNewerLocalQuantity = this._pendingQuantity !== null &&
            parseInt( cartState.inCartQuantity, 10 ) !== this._pendingQuantity;

        if ( this._isMounted && !hasNewerLocalQuantity ) {
            this.setState( {...cartState} );
        }
    }

    onQuantityChange ( quantity ) {
        this._pendingQuantity = quantity;

        if ( this._isMounted ) {
            this.setState( {inCartQuantity: quantity} );
        }

        this.onUpdateItem( quantity );
    }

    async _flushQuantityUpdate ( quantity ) {
        if ( this._quantityUpdatePromise ) {
            this._queuedQuantity = quantity;
            return this._quantityUpdatePromise;
        }

        this._quantityUpdatePromise = this._persistQuantity( quantity );

        let result;
        let queuedQuantity;
        try {
            result = await this._quantityUpdatePromise;
        }
        finally {
            this._quantityUpdatePromise = null;
            queuedQuantity = this._queuedQuantity;
            this._queuedQuantity = null;
        }

        if ( this._isMounted && queuedQuantity !== null && queuedQuantity !== quantity ) {
            return this._flushQuantityUpdate( queuedQuantity );
        }

        if ( result === undefined && this._pendingQuantity === quantity ) {
            this._pendingQuantity = null;
        }

        return result;
    }

    async _persistQuantity ( quantity ) {
        const {product} = this.props;

        if ( quantity <= 0 ) {
            return CartService.deleteLineItems( {product} );
        }

        return CartService.updateLineItems( {product, quantity} );
    }

    async onAddItem () {
        const {product, itemList} = this.props;

        this.setState( {processingAdd: true} );
        try {
            await CartService.addLineItems( [withItemListAttribution( {product, quantity: 1}, itemList )] );
        }
        catch ( err ) {
            appLogger.capture( err );
        }
        finally {
            if ( this._isMounted ) {
                this.setState( {processingAdd: false} );
            }
        }
    }

    renderActionComponent () {
        const {product, itemList, className, ...rest} = this.props;
        const {inCartQuantity, maxInputQuantity, processingAdd} = this.state;
        const lineQty = processingAdd ? 1 : parseInt( inCartQuantity );

        if ( lineQty > 0 ) {
            return (
                <QtyToggle
                    className={className}
                    value={lineQty}
                    min={0}
                    max={maxInputQuantity}
                    onChange={this.onQuantityChange}/>
            );
        }

        const btnClassName = cx( 'button w-full', className );

        return (
            <a
                data-sku={product.sku}
                data-item-list-id={itemList && itemList.id}
                data-item-list-name={itemList && itemList.name}
                className={btnClassName}
                onClick={this.onAddItem}
                {...rest}>
                <Icon.ui name="shopping-cart-add" className="text-xl mr-2"/>
                Add To Cart
            </a>
        );
    }

    render ( props ) {
        const {maxQuantityExceeded, maxQuantity} = this.state;

        return (
            <div className="w-full">
                {this.renderActionComponent()}
                {maxQuantityExceeded &&
                <p className="font-bold text-warning text-sm my-2">
                    <Icon.ui name="report-problem-triangle" className="text-sm mr-1"/>
                    {maxQuantity <= 0
                        ? `This item is not currently available for online orders.`
                        : `This item is limited to ${maxQuantity} per customer.`
                    }
                </p>
                }
            </div>
        );
    }
}

export default AddCartButton;
